import { t } from './language';

/**
 * Profil alanlarının doğrulaması — Supabase'den bağımsız saf kurallar,
 * böylece `npm run test:profile` ile doğrudan çalıştırılabiliyor.
 * Aynı sınırlar veritabanında da tanımlı (migrations/0015_profile_settings.sql).
 */

export const PROFILE_TEXT_LIMIT = 80;

/** İstemci fotoğrafı ≈256 px JPEG'e küçültüyor (≈20–60 KB); sınır bol tutuldu. */
export const AVATAR_LIMIT = 350_000;

const AVATAR_PATTERN = /^data:image\/(png|jpeg|webp);base64,([A-Za-z0-9+/]+={0,2})$/;

/** Boşlukları sadeleştirir, kontrol karakterlerini atar, uzunluğu sınırlar. */
export function cleanProfileText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, PROFILE_TEXT_LIMIT);
}

/**
 * Fotoğraf, istemcinin küçültüp ürettiği bir data: URL'si olmalı. Bildirilen
 * tür dosyanın imzasıyla da karşılaştırılıyor; resim olmayan içerik
 * veritabanına yazılmıyor.
 */
export function validateAvatarDataUrl(value: unknown): string {
  const invalid = () =>
    new Error(
      t(
        'Fotoğraf okunamadı. PNG, JPG ya da WebP bir görsel seçin.',
        'Could not read the photo. Choose a PNG, JPG or WebP image.'
      )
    );

  if (typeof value !== 'string') throw invalid();
  if (value.length > AVATAR_LIMIT) {
    throw new Error(t('Fotoğraf çok büyük.', 'The photo is too large.'));
  }
  const match = AVATAR_PATTERN.exec(value);
  if (!match) throw invalid();

  // Yalnızca baştaki imza baytları gerekiyor; `atob` hem Node'da hem tarayıcıda
  // (web uygulaması) var — Buffer yalnızca Node'da.
  let head: string;
  try {
    head = atob(match[2].slice(0, 24));
  } catch {
    throw invalid();
  }
  const kind = match[1];
  const isPng = head.startsWith('\x89PNG\r\n\x1a\n');
  const isJpeg = head.startsWith('\xff\xd8\xff');
  const isWebp = head.slice(0, 4) === 'RIFF' && head.slice(8, 12) === 'WEBP';
  const ok = (kind === 'png' && isPng) || (kind === 'jpeg' && isJpeg) || (kind === 'webp' && isWebp);
  if (!ok) throw invalid();
  return value;
}

/**
 * Şifre kuralları — Ayarlar > Genel > Şifre penceresi bunları canlı gösteriyor
 * (renderer/src/features/preferences/passwordRules.ts aynı listeyi tutuyor).
 * Kural listesi ekranda tek tek işaretlendiği için kimlikleriyle dönüyor.
 */
export type PasswordRuleId = 'length' | 'uppercase' | 'lowercase' | 'digit';

export const MIN_PASSWORD_LENGTH = 8;

export function checkPasswordRules(password: string): Record<PasswordRuleId, boolean> {
  const value = typeof password === 'string' ? password : '';
  return {
    length: value.length >= MIN_PASSWORD_LENGTH,
    uppercase: /\p{Lu}/u.test(value),
    lowercase: /\p{Ll}/u.test(value),
    digit: /\d/.test(value),
  };
}

export function isPasswordStrong(password: string): boolean {
  return Object.values(checkPasswordRules(password)).every(Boolean);
}
